import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HardDriveDownload, ShieldAlert } from 'lucide-react';
import { api, ApiError } from '../api/cliente.js';
import { Botao } from '../componentes/ui/Botao.js';
import { Pill } from '../componentes/ui/Pill.js';

/**
 * Destino externo do backup e a rotina de envio.
 *
 * Espelha o card de SMTP: config no banco, senha que não volta para a tela,
 * teste de ponta a ponta antes de confiar. O dump diário e a cópia cruzada já
 * existem — isto é a cópia FORA da infraestrutura, o maior risco que faltava
 * fechar.
 */

interface ConfigBackup {
  tipo: 'SFTP' | 'FTP' | 'FTPS';
  host: string;
  porta: number;
  usuario: string;
  temSenha: boolean;
  caminhoRemoto: string;
  retencao: number;
  horaEnvio: number;
  emailPolitica: 'SEMPRE' | 'SO_FALHA';
  ativo: boolean;
  ultimoTesteEm: string | null;
  ultimoTesteOk: boolean | null;
  ultimoTesteErro: string | null;
  ultimoEnvioEm: string | null;
  ultimoEnvioOk: boolean | null;
  ultimoEnvioErro: string | null;
  ultimoArquivo: string | null;
  ultimoBytes: number | null;
}

type Form = Omit<
  ConfigBackup,
  | 'temSenha'
  | 'ultimoTesteEm'
  | 'ultimoTesteOk'
  | 'ultimoTesteErro'
  | 'ultimoEnvioEm'
  | 'ultimoEnvioOk'
  | 'ultimoEnvioErro'
  | 'ultimoArquivo'
  | 'ultimoBytes'
> & { senha: string };

const PADRAO: Form = {
  tipo: 'SFTP',
  host: '',
  porta: 22,
  usuario: '',
  senha: '',
  caminhoRemoto: '/',
  retencao: 14,
  horaEnvio: 3,
  emailPolitica: 'SEMPRE',
  ativo: false,
};

export function SecaoBackupExterno() {
  const qc = useQueryClient();
  const [form, setForm] = useState<Form>(PADRAO);
  const [temSenha, setTemSenha] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['configuracoes', 'backup'],
    queryFn: () => api.get<ConfigBackup | null>('/configuracoes/backup'),
  });

  useEffect(() => {
    if (data) {
      setForm({ ...data, senha: '' });
      setTemSenha(data.temSenha);
    }
  }, [data]);

  const invalidar = () => qc.invalidateQueries({ queryKey: ['configuracoes', 'backup'] });

  const salvar = useMutation({
    mutationFn: () => api.put<ConfigBackup>('/configuracoes/backup', form),
    onSuccess: () => void invalidar(),
  });
  const testar = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; erro?: string }>('/configuracoes/backup/teste', { senha: form.senha || undefined }),
    onSuccess: () => void invalidar(),
  });
  const enviarAgora = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; arquivo: string | null; bytes: number; erro?: string }>(
        '/configuracoes/backup/enviar-agora',
      ),
    onSuccess: () => void invalidar(),
  });

  if (isLoading) return <div className="h-64 animate-pulse rounded-card bg-borda-tenue" />;

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const inseguro = form.tipo === 'FTP';

  return (
    <section className="rounded-card border border-borda bg-superficie p-6">
      <div className="mb-5 flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-chip bg-info-fundo text-info">
          <HardDriveDownload size={19} />
        </span>
        <div className="flex-1">
          <h2 className="text-[15px] font-extrabold leading-tight">Backup externo</h2>
          <p className="mt-0.5 text-[12.5px] text-texto-suave">
            Cópia do backup para fora da infraestrutura. Enquanto estiver inativo, nada é enviado.
          </p>
        </div>
        {data && (
          <Pill tom={data.ativo ? 'ok' : 'neutro'}>{data.ativo ? 'ativo' : 'inativo'}</Pill>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <Rotulo>Protocolo</Rotulo>
          <select
            value={form.tipo}
            onChange={(e) => {
              const tipo = e.target.value as Form['tipo'];
              set('tipo', tipo);
              // Sugere a porta padrão do protocolo ao trocar.
              set('porta', tipo === 'SFTP' ? 22 : 21);
            }}
            className="h-11 w-full rounded-controle border border-borda px-3 text-[14px] focus:border-marca focus:outline-none"
          >
            <option value="SFTP">SFTP (sobre SSH)</option>
            <option value="FTPS">FTPS (FTP sobre TLS)</option>
            <option value="FTP">FTP (sem criptografia)</option>
          </select>
        </label>

        <div className="grid grid-cols-[1fr_90px] gap-2">
          <label className="block">
            <Rotulo>Host</Rotulo>
            <input
              value={form.host}
              onChange={(e) => set('host', e.target.value)}
              placeholder="backup.exemplo.com"
              className="h-11 w-full rounded-controle border border-borda px-3 font-mono text-[13.5px] focus:border-marca focus:outline-none"
            />
          </label>
          <label className="block">
            <Rotulo>Porta</Rotulo>
            <input
              type="number"
              value={form.porta}
              onChange={(e) => set('porta', Number(e.target.value) || 0)}
              className="h-11 w-full rounded-controle border border-borda px-2 font-mono text-[13.5px] focus:border-marca focus:outline-none"
            />
          </label>
        </div>

        <label className="block">
          <Rotulo>Usuário</Rotulo>
          <input
            value={form.usuario}
            onChange={(e) => set('usuario', e.target.value)}
            className="h-11 w-full rounded-controle border border-borda px-3 text-[13.5px] focus:border-marca focus:outline-none"
          />
        </label>

        <label className="block">
          <Rotulo>Senha {temSenha && <span className="text-texto-mudo">(deixe em branco p/ manter)</span>}</Rotulo>
          <input
            type="password"
            value={form.senha}
            onChange={(e) => set('senha', e.target.value)}
            placeholder={temSenha ? '••••••••' : ''}
            autoComplete="new-password"
            className="h-11 w-full rounded-controle border border-borda px-3 text-[13.5px] focus:border-marca focus:outline-none"
          />
        </label>

        <label className="block sm:col-span-2">
          <Rotulo>Caminho remoto</Rotulo>
          <input
            value={form.caminhoRemoto}
            onChange={(e) => set('caminhoRemoto', e.target.value)}
            placeholder="/backups/consult-rdns"
            className="h-11 w-full rounded-controle border border-borda px-3 font-mono text-[13.5px] focus:border-marca focus:outline-none"
          />
        </label>

        <label className="block">
          <Rotulo>Hora do envio (0–23)</Rotulo>
          <input
            type="number"
            min={0}
            max={23}
            value={form.horaEnvio}
            onChange={(e) => set('horaEnvio', Math.max(0, Math.min(23, Number(e.target.value) || 0)))}
            className="h-11 w-full rounded-controle border border-borda px-3 font-mono text-[13.5px] focus:border-marca focus:outline-none"
          />
        </label>

        <label className="block">
          <Rotulo>Manter no destino (cópias)</Rotulo>
          <input
            type="number"
            min={1}
            max={365}
            value={form.retencao}
            onChange={(e) => set('retencao', Math.max(1, Number(e.target.value) || 1))}
            className="h-11 w-full rounded-controle border border-borda px-3 font-mono text-[13.5px] focus:border-marca focus:outline-none"
          />
        </label>

        <label className="block">
          <Rotulo>Avisar por e-mail</Rotulo>
          <select
            value={form.emailPolitica}
            onChange={(e) => set('emailPolitica', e.target.value as Form['emailPolitica'])}
            className="h-11 w-full rounded-controle border border-borda px-3 text-[14px] focus:border-marca focus:outline-none"
          >
            <option value="SEMPRE">Todo envio (sucesso e falha)</option>
            <option value="SO_FALHA">Só quando falhar</option>
          </select>
        </label>

        <label className="flex items-center gap-2 sm:col-span-2">
          <input
            type="checkbox"
            checked={form.ativo}
            onChange={(e) => set('ativo', e.target.checked)}
            className="size-4 accent-marca"
          />
          <span className="text-[13px] font-semibold">
            Rotina ativa — gera o dump no nó e envia ao destino todo dia na hora marcada
          </span>
        </label>
      </div>

      {inseguro && (
        <p className="mt-3 flex items-start gap-2 rounded-chip bg-aviso-fundo px-3 py-2 text-[12px] leading-snug text-aviso">
          <ShieldAlert size={14} className="mt-px shrink-0" />
          FTP puro trafega usuário, senha e o backup inteiro em texto claro. Prefira SFTP ou FTPS
          sempre que o destino permitir.
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Botao variante="primario" onClick={() => salvar.mutate()} carregando={salvar.isPending}>
          Salvar
        </Botao>
        <Botao onClick={() => testar.mutate()} carregando={testar.isPending} disabled={!data && !form.host}>
          Testar conexão
        </Botao>
        <Botao onClick={() => enviarAgora.mutate()} carregando={enviarAgora.isPending} disabled={!data?.ativo && !form.host}>
          Enviar agora
        </Botao>
      </div>

      {/* Resultados */}
      <div className="mt-3 space-y-1.5 text-[12px]">
        {salvar.error && (
          <Linha tom="erro">{(salvar.error as ApiError).mensagem ?? 'Falha ao salvar.'}</Linha>
        )}
        {testar.data &&
          (testar.data.ok ? (
            <Linha tom="ok">Conexão e escrita no destino confirmadas.</Linha>
          ) : (
            <Linha tom="erro">Teste falhou: {testar.data.erro}</Linha>
          ))}
        {enviarAgora.data &&
          (enviarAgora.data.ok ? (
            <Linha tom="ok">
              Enviado: {enviarAgora.data.arquivo} ({(enviarAgora.data.bytes / 1_048_576).toFixed(1)} MB).
            </Linha>
          ) : (
            <Linha tom="erro">Envio falhou: {enviarAgora.data.erro}</Linha>
          ))}
        {data?.ultimoEnvioEm && !enviarAgora.data && (
          <Linha tom={data.ultimoEnvioOk ? 'ok' : 'erro'}>
            Último envio {data.ultimoEnvioOk ? 'OK' : 'com falha'} em{' '}
            {new Date(data.ultimoEnvioEm).toLocaleString('pt-BR')}
            {data.ultimoEnvioOk && data.ultimoArquivo ? ` — ${data.ultimoArquivo}` : ''}
            {!data.ultimoEnvioOk && data.ultimoEnvioErro ? ` — ${data.ultimoEnvioErro}` : ''}
          </Linha>
        )}
      </div>
    </section>
  );
}

function Rotulo({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
      {children}
    </span>
  );
}

function Linha({ tom, children }: { tom: 'ok' | 'erro'; children: React.ReactNode }) {
  return (
    <p className={`font-semibold ${tom === 'ok' ? 'text-sucesso' : 'text-erro'}`}>{children}</p>
  );
}
